import { AnimatePresence, motion } from 'framer-motion';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addFavoriteProfessional } from '../../../../actions';
import { Box, Typography } from '@tempmee/react';
import { CenteredSpinner } from '~/commonComponents';
import { colors } from '@tempmee/tailwind';
import { ProfessionalHubRedesign } from '../../../../constants/ff.posthog';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useScrollRestore } from '../../../../hooks/useScrollRestore';
import Tooltip from '../../../Tooltip';
import { CardActionsHeader } from '../CardActionsHeader';
import { EmptyFilteredList } from '../EmptyFilteredList';
import { HubCardList } from '../shared/HubCardList';
import { ProfessionalCard } from '../ProfessionalCard';
import { EmptyRecentProfessionals } from './EmptyRecentProfessionals';
import { useRecentProfessionalsList } from './hooks/useRecentProfessionalsList';
// New design
import { ProfessionalCardActions } from './ProfessionalCardActions';
import { ProfessionalCardDetails } from './ProfessionalCardDetails';
// Legacy design
import { ProfessionalCardActionsLegacy } from './ProfessionalCardActionsLegacy';
import { ProfessionalCardDetailsLegacy } from './ProfessionalCardDetailsLegacy';

export const MAX_RECENT_PROFESSIONALS = 6;

const getRating = (item) => {
  if (!item?.professional?.overallProfessionalRating && !item?.ratingCount) {
    return undefined;
  }
  return {
    averageRating:
      item?.professional?.overallProfessionalRating?.averageRating || item?.ratingCount,
    interpersonalSkills:
      item?.professional?.overallProfessionalRating?.averageInterpersonalSkills ||
      item?.ratingCount,
    professionalSkills:
      item?.professional?.overallProfessionalRating?.averageProfessionalSkills || item?.ratingCount,
    timeManagement:
      item?.professional?.overallProfessionalRating?.averageTimeManagement || item?.ratingCount,
  };
};

export const RecentProfessionalsList = ({ onFavoriteProfessional } = {}) => {
  const dispatch = useDispatch();
  const scrollRef = useRef(null);
  const isNewDesign = useFeatureFlag(ProfessionalHubRedesign);
  const { recentProfessionals, isLoading, isEmpty, isFiltered } = useRecentProfessionalsList();

  useScrollRestore('RecentProfessionalsList', scrollRef);

  if (isLoading) {
    return <CenteredSpinner />;
  }

  if (isEmpty) {
    if (isFiltered) {
      return <EmptyFilteredList />;
    }
    return <EmptyRecentProfessionals />;
  }

  // Legacy design (feature flag OFF)
  if (!isNewDesign) {
    return (
      <div style={{ width: '100%' }}>
        <Typography
          as="div"
          italic
          color={colors.neutral[500]}
          style={{ position: 'relative', textAlign: 'center' }}
        >
          This section of the Professionals Hub shows the most recent professionals to have worked
          at your office.
          <span
            style={{
              marginLeft: 5,
              // LEGACY-HACK (cleanup candidate): nudges the info tooltip; absolute-positioned compensation.
              marginTop: -2,
              position: 'absolute',
              fontStyle: 'normal',
              fontWeight: 700,
            }}
          >
            <Tooltip
              content={`
                The recent professionals list displays the last ${MAX_RECENT_PROFESSIONALS}
                professionals to have completed shifts at your office.
                This list excludes favorites and blocked professionals.
              `}
            />
          </span>
        </Typography>

        <div
          style={{
            maxWidth: '827px',
            marginTop: 32,
            display: 'flex',
            justifyContent: 'center',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <motion.div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
            layout
          >
            <CardActionsHeader actions={['Invite', 'Favorite', 'Block']} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
              <AnimatePresence>
                {recentProfessionals?.map((item) => (
                  <ProfessionalCard
                    key={item.id}
                    user={{
                      id: item?.professional?.id,
                      fullname: item?.professional?.full_name,
                      professions: item?.professional?.professions,
                      profession: item.profession,
                      profileImage: item.professional?.image,
                      datetime: item?.job_datetime,
                      isAvailable: item?.isAvailable ?? true,
                      jobId: item.id,
                      rating: getRating(item),
                    }}
                    Details={ProfessionalCardDetailsLegacy}
                    Actions={ProfessionalCardActionsLegacy}
                    showRemoveIcon={false}
                  />
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  // New design (feature flag ON)
  return (
    <HubCardList
      headerActions={['Block', 'Invite']}
      headerRightOffset={52}
      scrollRef={scrollRef}
      intro="This section of the Professionals Hub shows the most recent professionals to have worked at your office."
    >
      {recentProfessionals?.map((item) => (
        <ProfessionalCard
          key={item.id}
          user={{
            id: item?.professional?.id,
            fullname: item?.professional?.full_name,
            professions: item?.professional?.professions,
            profession: item.profession,
            profileImage: item.professional?.image,
            datetime: item?.job_datetime,
            isAvailable: item?.isAvailable ?? true,
            jobId: item.id,
            yearsOfExperience: item.professional?.yearsOfExperience,
            badges: item.professional?.badges ?? [],
            rating: getRating(item),
          }}
          Details={ProfessionalCardDetails}
          Actions={ProfessionalCardActions}
          showRemoveIcon={false}
          isNewDesign
          onFavorite={() => {
            onFavoriteProfessional?.(item?.professional?.full_name);
            dispatch(addFavoriteProfessional(item?.professional?.id));
          }}
        />
      ))}
    </HubCardList>
  );
};
